import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';

const ToastCtx = createContext(null);

export function useToast() {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}

const slideIn = keyframes`
  from { transform: translateY(8px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const Container = styled.div`
  position: fixed;
  right: 16px;
  bottom: 16px;
  display: grid;
  gap: 10px;
  z-index: 1000;
`;

const ToastCard = styled.div`
  min-width: 280px;
  max-width: 420px;
  background: #111827;
  color: #fff;
  border-radius: 12px;
  padding: 12px 14px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.25);
  animation: ${slideIn} 160ms ease-out;
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: start;
  border: 1px solid rgba(255,255,255,0.06);
`;

const Title = styled.div`
  font-weight: 700;
  margin-bottom: 2px;
`;

const Desc = styled.div`
  opacity: 0.9;
  font-size: 14px;
`;

const Close = styled.button`
  appearance: none;
  background: transparent;
  border: none;
  color: #cbd5e1;
  font-weight: 700;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 8px;
  &:hover{ background: rgba(255,255,255,0.08); color: #fff; }
`;

export default function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(1);

  const remove = useCallback((id) => setToasts((t) => t.filter(x => x.id !== id)), []);

  const push = useCallback((toast) => {
    const id = idRef.current++;
    const t = { id, timeout: 3000, variant: 'default', ...toast };
    setToasts((prev) => [...prev, t]);
    if (t.timeout) {
      setTimeout(() => remove(id), t.timeout);
    }
    return id;
  }, [remove]);

  const api = useMemo(() => ({
    toast: (msg) => push(typeof msg === 'string' ? { title: msg } : msg),
    success: (title, description) => push({ title, description, variant: 'success' }),
    error: (title, description) => push({ title, description, variant: 'error', timeout: 5000 }),
  }), [push]);

  return (
    <ToastCtx.Provider value={api}>
      {children}
      <Container>
        {toasts.map(t => (
          <ToastCard key={t.id} role="status" aria-live="polite" style={{
            background: t.variant === 'error' ? '#7f1d1d' : t.variant === 'success' ? '#064e3b' : '#111827',
            borderColor: 'rgba(255,255,255,0.06)'
          }}>
            <div>
              {t.title && <Title>{t.title}</Title>}
              {t.description && <Desc>{t.description}</Desc>}
            </div>
            <Close aria-label="Close" onClick={() => remove(t.id)}>×</Close>
          </ToastCard>
        ))}
      </Container>
    </ToastCtx.Provider>
  );
}
